import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Bug,
  ArrowLeft,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { useOnboarding } from '../context/OnboardingContext';
import { PleriClient } from '../../Pleri/client/PleriClient';
import { useUpdateOnboardingPreference } from '../../../api/onboarding-preferences';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

interface ActionCard {
  id: string;
  title: string;
  description: string;
  prompt: string;
  icon: React.ReactNode;
}

// Each prompt is sent to the agent, so it stays English.
const actionCards = (t: TFunction<'pleri/onboarding'>): ActionCard[] => [
  {
    id: 'cloud-security-assessment',
    title: t('runAComprehensiveCloudSecurityAssessmentFor'),
    description: t('getACompleteOverviewOfYourCloud'),
    prompt: 'Run a comprehensive cloud security assessment.',
    icon: <ShieldCheck className="h-6 w-6" />,
  },
  {
    id: 'vulnerability-assessment',
    title: t('runAVulnerabilityAssessment'),
    description: t('identifyAndAnalyzeSecurityVulnerabilitiesAcross'),
    prompt: 'Run a vulnerability assessment.',
    icon: <Bug className="h-6 w-6" />,
  },
];

export const ChooseWhatToDoFirstStep: React.FC<{
  onComplete: () => void;
}> = ({ onComplete }) => {
  const { t } = useTranslation('pleri/onboarding');
  const { updateData, goToPreviousStep, canGoBack, data } = useOnboarding();
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const updateOnboardingPreference = useUpdateOnboardingPreference();

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to go back
      if (e.key === 'Escape' && canGoBack) {
        e.preventDefault();
        goToPreviousStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canGoBack, goToPreviousStep]);

  const handleActionSelect = async (action: ActionCard) => {
    setSelectedCard(action.id);

    // Update onboarding data
    updateData({ selectedAction: action.id });

    // Mark onboarding as completed using preferences API
    try {
      await updateOnboardingPreference.mutateAsync({
        status: 'completed',
      });
    } catch (error) {
      console.error('Failed to mark onboarding as completed:', error);
      // Continue anyway - don't block navigation
    }

    // Start conversation with the selected action - PleriClient will handle navigation
    PleriClient.updateChatInput({
      prompt: action.prompt,
      autoSubmit: true,
      metrics: {
        eventLocation: 'onboarding',
        eventName: 'onboardingActionClick',
        eventType: 'autoSubmitQuery',
        eventValue: action.id,
        eventDetails: {
          title: action.title,
        },
      },
    });

    // Complete onboarding - PleriClient will handle opening chat
    onComplete();
  };

  const containerVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut' as const,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
      },
    }),
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="mx-auto max-w-3xl px-8 py-8"
    >
      <div className="mb-8">
        <p
          className="text-base leading-relaxed text-gray-600 dark:text-gray-400"
          style={{ fontFamily: 'Archivo, sans-serif' }}
        >
          {t('thanksThatAllINeedForNow', { name: data.name || t('there') })}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {actionCards(t).map((action, index) => {
          const isSelected = selectedCard === action.id;
          return (
            <motion.div
              key={action.id}
              custom={index}
              initial="hidden"
              animate="visible"
              variants={itemVariants}
              whileHover={{ scale: 1.02, y: -4 }}
              whileTap={{ scale: 0.98 }}
              className="group relative"
            >
              <div
                className={`relative flex h-full flex-col overflow-hidden rounded-2xl border-2 bg-white shadow-lg transition-all duration-300 dark:bg-gray-900 ${
                  isSelected
                    ? 'border-[#FF732E] shadow-xl shadow-[#FF732E]/20'
                    : 'border-gray-200 hover:border-[#FF732E] hover:shadow-xl dark:border-gray-700'
                }`}
              >
                {/* Gradient overlay on hover */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br from-[#FF732E]/5 to-transparent opacity-0 transition-opacity duration-300 ${
                    isSelected || 'group-hover:opacity-100'
                  }`}
                />

                <div className="relative flex h-full flex-col p-6">
                  {/* Icon with background */}
                  <div className="mb-5 flex items-start justify-between">
                    <div
                      className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl transition-all duration-300 ${
                        isSelected
                          ? 'bg-[#FF732E] text-white shadow-lg shadow-[#FF732E]/30'
                          : 'bg-gradient-to-br from-[#FF732E]/10 to-[#FF732E]/5 text-[#FF732E] group-hover:scale-110 group-hover:bg-[#FF732E] group-hover:text-white group-hover:shadow-lg'
                      }`}
                    >
                      {action.icon}
                    </div>
                    {!isSelected && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileHover={{ opacity: 1, scale: 1 }}
                        className="flex h-6 w-6 items-center justify-center rounded-full bg-[#FF732E]/10 text-[#FF732E] opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <Sparkles className="h-4 w-4" />
                      </motion.div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="mb-6 flex-1">
                    <h3
                      className={`mb-3 text-xl leading-tight font-semibold text-gray-900 transition-colors dark:text-gray-100 ${
                        isSelected
                          ? 'text-[#FF732E]'
                          : 'group-hover:text-[#FF732E]'
                      }`}
                      style={{ fontFamily: 'Archivo, sans-serif' }}
                    >
                      {action.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {action.description}
                    </p>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleActionSelect(action)}
                    disabled={isSelected}
                    className={`group/btn flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-base font-semibold text-white transition-all duration-300 ${
                      isSelected
                        ? 'cursor-wait bg-gray-400'
                        : 'bg-gradient-to-r from-[#FF732E] to-[#FF8C42] shadow-md hover:shadow-lg hover:shadow-[#FF732E]/30 active:scale-95'
                    }`}
                    style={{ fontFamily: 'Archivo, sans-serif' }}
                  >
                    {isSelected ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: 'linear',
                          }}
                        >
                          <Sparkles className="h-5 w-5" />
                        </motion.div>
                        {t('starting')}
                      </>
                    ) : (
                      <>
                        {t('getStarted')}
                        <ArrowRight className="h-5 w-5 transition-transform group-hover/btn:translate-x-1" />
                      </>
                    )}
                  </button>
                </div>

                {/* Shine effect on hover */}
                {!isSelected && (
                  <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
      {/* Back button */}
      {canGoBack && (
        <div className="mt-8 flex justify-start">
          <button
            onClick={goToPreviousStep}
            className="group flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-800"
            title={t('goBackEsc')}
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            {t('common:back')}
          </button>
        </div>
      )}
    </motion.div>
  );
};
